import { useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const CONTENT = {
  it: {
    title: "Privacy & Cookie Policy",
    updated: "Ultimo aggiornamento: settembre 2026",
    sections: [
      {
        h: "Titolare del trattamento",
        body: [
          "OGStay · Sede legale: Tortolì (OG), Ogliastra — Sardegna, Italia · P.IVA 01667620916 · Email: info@ogstay.com",
        ],
      },
      {
        h: "Quali dati raccogliamo",
        body: [
          "Richiesta di valutazione immobile: nome, cognome, email, telefono, località e caratteristiche dell'immobile, obiettivi e preferenze di ricontatto.",
          "Richieste di informazioni su case ed esperienze: nome, email e messaggio.",
          "Newsletter e guida gratuita: nome ed email.",
          "Concierge AI: i messaggi che scrivi in chat, necessari per risponderti.",
          "Dati di navigazione tecnici: indirizzo IP e log di sistema, per sicurezza e funzionamento.",
        ],
      },
      {
        h: "Perché li usiamo",
        body: [
          "Rispondere alle tue richieste e preparare la valutazione della tua proprietà (misure precontrattuali).",
          "Inviarti la guida o la newsletter, solo se richieste (consenso, revocabile in qualsiasi momento).",
          "Migliorare il sito e garantirne la sicurezza (legittimo interesse).",
        ],
      },
      {
        h: "Chi tratta i dati",
        body: [
          "Email transazionali: Resend, tramite la piattaforma Emergent.",
          "Concierge AI: il testo della chat viene elaborato da OpenAI tramite la piattaforma Emergent per generare le risposte.",
          "Prenotazioni: il portale Kross Booking (ogstay.kross.travel e vr.krossbooking.com) è un servizio esterno con propria privacy policy.",
          "Mappe: OpenStreetMap. Font: Google Fonts. Immagini: CDN Wix.",
        ],
      },
      {
        h: "Conservazione",
        body: [
          "Richieste e contatti: fino a 24 mesi dall'ultimo contatto.",
          "Conversazioni del concierge: fino a 12 mesi.",
          "Puoi chiedere la cancellazione in qualsiasi momento.",
        ],
      },
      {
        h: "I tuoi diritti",
        body: [
          "Puoi chiedere accesso, rettifica, cancellazione, limitazione o opposizione al trattamento scrivendo a info@ogstay.com.",
          "Puoi proporre reclamo al Garante per la protezione dei dati personali (garanteprivacy.it).",
        ],
      },
      {
        h: "Cookie e strumenti simili",
        body: [
          "Questo sito usa solo cookie e archiviazione locale tecnici: scelta del banner, sessione della chat concierge, accesso all'area riservata.",
          "Non usiamo cookie di profilazione o pubblicitari di nostra iniziativa.",
          "Il portale di prenotazione (Kross) e la mappa (OpenStreetMap) sono servizi di terze parti che possono impostare propri cookie secondo le rispettive policy.",
        ],
      },
    ],
  },
  en: {
    title: "Privacy & Cookie Policy",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "Data controller",
        body: [
          "OGStay · Registered office: Tortolì (OG), Ogliastra — Sardinia, Italy · VAT IT01667620916 · Email: info@ogstay.com",
        ],
      },
      {
        h: "What data we collect",
        body: [
          "Property valuation requests: name, surname, email, phone, property location and features, goals and contact preferences.",
          "Information requests about homes and experiences: name, email and message.",
          "Newsletter and free guide: name and email.",
          "AI concierge: the messages you write in the chat, needed to answer you.",
          "Technical browsing data: IP address and system logs, for security and operation.",
        ],
      },
      {
        h: "Why we use it",
        body: [
          "To answer your requests and prepare your property valuation (pre-contractual measures).",
          "To send the guide or newsletter, only if requested (consent, revocable at any time).",
          "To improve the site and keep it secure (legitimate interest).",
        ],
      },
      {
        h: "Who processes the data",
        body: [
          "Transactional email: Resend, via the Emergent platform.",
          "AI concierge: chat text is processed by OpenAI via the Emergent platform to generate answers.",
          "Bookings: the Kross Booking portal (ogstay.kross.travel and vr.krossbooking.com) is an external service with its own privacy policy.",
          "Maps: OpenStreetMap. Fonts: Google Fonts. Images: Wix CDN.",
        ],
      },
      {
        h: "Retention",
        body: [
          "Requests and contacts: up to 24 months from the last contact.",
          "Concierge conversations: up to 12 months.",
          "You can request deletion at any time.",
        ],
      },
      {
        h: "Your rights",
        body: [
          "You can request access, rectification, deletion, restriction or objection by writing to info@ogstay.com.",
          "You can lodge a complaint with the Italian Data Protection Authority (garanteprivacy.it).",
        ],
      },
      {
        h: "Cookies and similar tools",
        body: [
          "This site uses only technical cookies and local storage: banner choice, concierge chat session, reserved-area access.",
          "We do not use our own profiling or advertising cookies.",
          "The booking portal (Kross) and the map (OpenStreetMap) are third-party services that may set their own cookies under their own policies.",
        ],
      },
    ],
  },
};

export default function PrivacyPage() {
  const { lang } = useLanguage();
  const c = CONTENT[lang];

  useEffect(() => {
    const prev = document.title;
    document.title = `${c.title} | OGStay`;
    return () => {
      document.title = prev;
    };
  }, [c]);

  return (
    <div data-testid="privacy-page" className="bg-warm pb-24 pt-36 sm:pt-44">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <p className="kicker text-navy">
            <span className="h-px w-10 bg-sky" />
            OGStay
          </p>
          <h1 data-testid="privacy-title" className="mt-5 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {c.title}
          </h1>
          <p className="mt-4 text-sm font-semibold text-mute">{c.updated}</p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {c.sections.map((sec, i) => (
            <motion.section
              key={sec.h}
              data-testid={`privacy-section-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <h2 className="font-display text-2xl font-semibold text-navy">{sec.h}</h2>
              <ul className="mt-4 space-y-3">
                {sec.body.map((p, j) => (
                  <li key={j} className="border-l-2 border-sky/40 pl-4 text-sm leading-relaxed text-ink/85 sm:text-base">
                    {p}
                  </li>
                ))}
              </ul>
            </motion.section>
          ))}
        </div>
      </div>
    </div>
  );
}
